import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CACHE_CONFIG } from './constants/query.constants';
import { isNetworkError } from './lib';
import './index.css';
import { Toaster } from './components/ui/toaster';
import GlobalLoader from './components/loaders/global-loader';
import Dashboard from './pages/dashboard';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: CACHE_CONFIG.MEDIUM.staleTime, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
      // Throw network errors so ErrorBoundary can catch them
      throwOnError: (error) => isNetworkError(error),
    },
  },
});

// Lazy-loaded components for code splitting
const LoginPage = lazy(() => import('./pages/login-page'));
const MembersPage = lazy(() => import('./pages/member-page'));
const ResetPassword = lazy(() => import('./components/shared/reset-password'));
const ProfilePage = lazy(() => import('./pages/profile-page'));
const UsersPage = lazy(() => import('./pages/users-page'));
const BlogsPage = lazy(() => import('./pages/blogs-page'));
const CreateBlogPage = lazy(() => import('./pages/create-blog-page'));
const EditBlogPage = lazy(() => import('./pages/edit-blog-page'));
const DiscountsPage = lazy(() => import('./pages/discounts-page'));
const BannersPage = lazy(() => import('./pages/banners-page'));
const SessionAssignmentRequestsPage = lazy(() => import('./pages/session-assignment-request'));
const EarningsSetupPage = lazy(() => import('./pages/configuration/earnings-setup'));
const PerformanceRulesPage = lazy(() => import('./pages/configuration/performance-rules'));
const PartnersPage = lazy(() => import('./pages/partners-page'));
const PartnerProfilePage = lazy(() => import('./pages/partner-profile-page'));
const SessionsPage = lazy(() => import('./pages/sessions-page'));
const MonthlyEarningsPage = lazy(() => import('./pages/earnings/monthly-earnings-page'));
const WalletPayoutsPage = lazy(() => import('./pages/earnings/wallet-payouts-page'));
const OnboardingPage = lazy(() => import('./pages/tasks/onboarding-page'));
const OperationsPage = lazy(() => import('./pages/tasks/operations-page'));
const LeavesPage = lazy(() => import('./pages/leaves-page'));
const VetsPage = lazy(() => import('./pages/vets-page'));
const VetDetailPage = lazy(() => import('./pages/vet-detail-page'));
const VetSubmissionsPage = lazy(() => import('./pages/vet-submissions-page'));
const VetSubmissionDetailPage = lazy(() => import('./pages/vet-submission-detail-page'));
const VetReviewsPage = lazy(() => import('./pages/vet-reviews-page'));
const NotFoundPage = lazy(() => import('./pages/not-found-page').then(module => ({ default: module.NotFoundPage })));
const SubscriptionsPage = lazy(() => import('./pages/subscriptions-page'));
const ProductsPage = lazy(() => import('./pages/products-page'));
const SupplyPlanningPage = lazy(() => import('./pages/supply-planning-page').then(module => ({ default: module.SupplyPlanningPage })));
const HubEditorPage = lazy(() => import('./pages/hub-editor-page').then(module => ({ default: module.HubEditorPage })));
const DocsIndexPage = lazy(() => import('./pages/docs/docs-index-page'));
const SessionRequestsDocPage = lazy(() => import('./pages/docs/session-requests-page'));
const OrdersPage = lazy(() => import('./pages/orders-page').then(module => ({ default: module.OrdersPage })));
const DashboardPage = lazy(() => import('./pages/dashboard-page'));
const TicketsPage = lazy(() => import('./pages/tickets-page'));
const CreateTicketPage = lazy(() => import('./pages/create-ticket-page'));
const TicketDetailPage = lazy(() => import('./pages/ticket-detail-page'));
const TriggerWeeklyPayoutPage = lazy(() => import('./pages/trigger-weekly-payout-page'));

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Routes>
          {/* Non-dashboard routes with full-screen loading */}
          <Route path="/" element={
            <Suspense fallback={<GlobalLoader />}>
              <LoginPage />
            </Suspense>
          } />
          <Route path="/reset-password" element={
            <Suspense fallback={<GlobalLoader />}>
              <ResetPassword />
            </Suspense>
          } />
          <Route path="/profile" element={
            <Suspense fallback={<GlobalLoader />}>
              <ProfilePage />
            </Suspense>
          } />

          {/* Dashboard routes - loading handled inside Dashboard component */}
          <Route path="/dashboard" element={<Dashboard />}>
            <Route index element={<DashboardPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/new" element={<CreateTicketPage />} />
            <Route path="tickets/:ticketId" element={<TicketDetailPage />} />
            <Route path="members" element={<MembersPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/:productId" element={<ProductsPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:orderId" element={<OrdersPage />} />
            <Route path="blogs">
              <Route index element={<BlogsPage />} />
              <Route path="create" element={<CreateBlogPage />} />
              <Route path="edit/:slug" element={<EditBlogPage />} />
            </Route>
            <Route path='banners' element={<BannersPage />} />
            <Route path='discounts' element={<DiscountsPage />} />
            <Route path='sessions' element={<SessionsPage />} />
            <Route path='sessions/:sessionId' element={<SessionsPage />} />
            <Route path='session-assignment-requests' element={<SessionAssignmentRequestsPage />} />
            <Route path='session-assignment-requests/:requestId' element={<SessionAssignmentRequestsPage />} />
            <Route path="configuration">
              <Route path="earnings-setup" element={<EarningsSetupPage />} />
              <Route path="performance-rules" element={<PerformanceRulesPage />} />
            </Route>
            <Route path='tasks'>
              <Route path='onboarding' element={<OnboardingPage />} />
              <Route path='operations' element={<OperationsPage />} />
            </Route>
            <Route path='earnings'>
              <Route path='monthly-earnings' element={<MonthlyEarningsPage />} />
              <Route path='wallet-payouts' element={<WalletPayoutsPage />} />
            </Route>
            <Route path='partners/:partnerId' element={<PartnerProfilePage />} />
            <Route path='partners' element={<PartnersPage />} />
            <Route path='leaves' element={<LeavesPage />} />
            <Route path='vets'>
              <Route index element={<VetsPage />} />
              <Route path='submissions' element={<VetSubmissionsPage />} />
              <Route path='submissions/:submissionId' element={<VetSubmissionDetailPage />} />
              <Route path='reviews' element={<VetReviewsPage />} />
              <Route path=':vetId' element={<VetDetailPage />} />
            </Route>
            <Route path='supply-planning' element={<SupplyPlanningPage />} />
            <Route path='hub-editor' element={<HubEditorPage />} />
            {/* Hidden page - no sidebar entry, reachable only via direct URL */}
            <Route path='partner-payouts/trigger-weekly-payout-request' element={<TriggerWeeklyPayoutPage />} />
            <Route path='docs'>
              <Route index element={<DocsIndexPage />} />
              <Route path='session-requests' element={<SessionRequestsDocPage />} />
            </Route>
            {/* Catch-all route for 404 - must be last */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <Toaster />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
